import { useState, useCallback, type FormEvent } from 'react';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';

interface Company { id: number; name: string; }

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ROLES = [
  { id: 1, name: 'superadmin', label: 'Super Admin' },
  { id: 2, name: 'company_admin', label: 'Company Admin' },
  { id: 3, name: 'kyc_officer', label: 'KYC Officer' },
  { id: 4, name: 'customer', label: 'Customer' },
];

export default function AddUserModal({ open, onClose, onSuccess }: AddUserModalProps) {
  const { user } = useAuth();
  const { post } = useApi();
  const isSuperAdmin = user?.role === 'superadmin';

  const { data: companiesData } = useApiQuery<{ companies: Company[] }>({
    endpoint: '/companies',
    enabled: isSuperAdmin && open,
  });

  const companies = companiesData?.companies || [];

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    roleId: 2,
    companyId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      roleId: 2,
      companyId: '',
    });
    setError(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const updateField = useCallback(
    (field: string, value: string | number) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);

      try {
        const body: Record<string, unknown> = {
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
          roleId: Number(form.roleId),
        };
        if (form.password.trim()) body.password = form.password.trim();
        if (isSuperAdmin && form.companyId) body.companyId = Number(form.companyId);

        await post('/users', body);
        reset();
        onSuccess();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create user');
      } finally {
        setLoading(false);
      }
    },
    [form, isSuperAdmin, post, reset, onSuccess]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <h2 className="text-lg font-bold text-stone-900">Add User</h2>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">First Name</label>
              <input
                required
                type="text"
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Last Name</label>
              <input
                required
                type="text"
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => updateField('password', e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              placeholder="Leave blank for default (Welcome123!)"
            />
            <p className="text-stone-400 text-xs mt-1">Min 8 characters. Leave empty to use default.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                placeholder="+1 234 567 8900"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Role</label>
              <select
                value={form.roleId}
                onChange={(e) => updateField('roleId', Number(e.target.value))}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
              >
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isSuperAdmin && (
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Company</label>
              <select
                value={form.companyId}
                onChange={(e) => updateField('companyId', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
              >
                <option value="">Select a company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-stone-400 text-xs mt-1">Required for superadmins. Assigns the user to this company.</p>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="ri-add-line" />
                  Add User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
